import React from 'react';
import { Download, Printer, X, Receipt } from 'lucide-react';

const ReceiptGenerator = ({ expense, group, isOpen, onClose }) => {
  const generateReceipt = () => {
    const receiptContent = `
SPLITWISE EXPENSE RECEIPT
========================

Group: ${group?.name || 'N/A'}
Receipt Generated: ${new Date().toLocaleDateString('en-IN')}
Time: ${new Date().toLocaleTimeString('en-IN')}

EXPENSE DETAILS
---------------
Description: ${expense?.description || 'N/A'}
Total Amount: ₹${expense?.amount || 0}
Paid By: ${expense?.payerName || 'N/A'}
Date: ${expense?.date ? new Date(expense.date).toLocaleDateString('en-IN') : 'N/A'}

SPLIT CALCULATION
-----------------
Total Members: ${group?.members?.length || 1}
Amount Per Person: ₹${((expense?.amount || 0) / (group?.members?.length || 1)).toFixed(2)}

GROUP MEMBERS
-------------
${group?.members?.map((m, i) => `${i + 1}. ${m.name} (${m.email})`).join('\n') || 'No members listed'}

PAYMENT BREAKDOWN
-----------------
${group?.members?.map(m => 
  `${m.name}: ${m.name === expense?.payerName ? '₹' + expense?.amount + ' (PAID)' : '₹' + ((expense?.amount || 0) / (group?.members?.length || 1)).toFixed(2) + ' (OWES)'}`
).join('\n') || ''}

Thank you for using SplitWise!
Generated automatically - splitwise.app
    `.trim();

    return receiptContent;
  };

  const downloadReceipt = () => {
    const content = generateReceipt();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `splitwise-receipt-${expense?.description?.replace(/\s+/g, '-') || 'expense'}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    if (window.showNotification) {
      window.showNotification('success', 'Receipt downloaded successfully!');
    }
  };

  const printReceipt = () => {
    const content = generateReceipt();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>SplitWise Receipt</title>
          <style>
            body { 
              font-family: 'Courier New', monospace; 
              padding: 20px; 
              max-width: 600px; 
              margin: 0 auto;
              line-height: 1.4;
            }
            pre { 
              white-space: pre-wrap; 
              word-wrap: break-word;
            }
            @media print {
              body { padding: 10px; }
            }
          </style>
        </head>
        <body>
          <pre>${content}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-xl rounded-lg bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center">
            <Receipt className="h-6 w-6 mr-2 text-teal-600" />
            Generate Receipt
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg mb-6 border-2 border-dashed border-gray-300">
          <div className="text-center mb-6">
            <h4 className="font-bold text-lg text-gray-900">SPLITWISE RECEIPT</h4>
            <div className="w-16 h-0.5 bg-gray-400 mx-auto mt-2"></div>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Group:</span>
              <span className="font-semibold">{group?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-semibold">{new Date().toLocaleDateString('en-IN')}</span>
            </div>
            
            <div className="border-t border-gray-300 pt-3 mt-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Description:</span>
                <span className="font-semibold">{expense?.description}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-semibold text-lg">₹{expense?.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Paid by:</span>
                <span className="font-semibold">{expense?.payerName}</span>
              </div>
            </div>
            
            <div className="border-t border-gray-300 pt-3 mt-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Members:</span>
                <span className="font-semibold">{group?.members?.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Per Person:</span>
                <span className="font-semibold text-lg text-teal-600">
                  ₹{((expense?.amount || 0) / (group?.members?.length || 1)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-6 text-xs text-gray-500">
            Generated by SplitWise • {new Date().toLocaleString('en-IN')}
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={downloadReceipt}
            className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 transition-colors shadow-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Receipt
          </button>
          <button
            onClick={printReceipt}
            className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptGenerator;